<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { xssColors } from '@/Common/constants';
  import { XSSDeviationCategory, xssDeviationToCategory, type XSSDetails } from '@/Util/utils';
  import { fmtDate } from './format';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // execution.daily_component_compliance of a report, weeks across and days down. Each day has four rings: the day
  // overall (its worst component), Low, High and Peak, coloured by the planner's classification.
  export let days: any[] = [];

  type Kind = 'good' | 'warn' | 'alert' | 'rest' | 'rest_trained';
  // Four rings: the day overall, Low, High, Peak. The kind says it in words (and in the ring's shape), not only in its colour.
  type Day = { date: string; rest: boolean; rings: { kind: Kind; color: string }[] | null } | null;
  type Week = { number: number; from: string; to: string; days: Day[] };

  const GREY = '#9CA3AF';
  const VIOLET = '#8B5CF6';
  const colour = (c: XSSDeviationCategory) => (c === XSSDeviationCategory.Good ? xssColors.deviationGood : c === XSSDeviationCategory.Warn ? xssColors.deviationWarn : xssColors.deviationAlert);
  const asDetails = (x: any): XSSDetails => ({ xss: x?.total ?? 0, xlss: x?.low ?? 0, xhss: x?.high ?? 0, xpss: x?.peak ?? 0 });
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const kindOf = (c: XSSDeviationCategory): Kind => (c === XSSDeviationCategory.Good ? 'good' : c === XSSDeviationCategory.Warn ? 'warn' : 'alert');
  const ring = (kind: Kind, color: string) => ({ kind, color });

  function ringsOf(e: any): { kind: Kind; color: string }[] | null {
    // A day counts as a training day when it prescribes at least 1 XSS: the same rule as the counts above.
    const prescribed = e.prescribed?.total ?? 0;
    if (prescribed >= 1) {
      const c = xssDeviationToCategory(asDetails(e.actual), asDetails(e.prescribed));
      return [c.xss, c.xlss, c.xhss, c.xpss].map((x) => ring(kindOf(x.suitability), colour(x.suitability)));
    }
    if (e.rest_day) return (e.actual?.total ?? 0) >= 1 ? Array(4).fill(ring('rest_trained', VIOLET)) : Array(4).fill(ring('rest', GREY));
    return null;
  }

  $: weeks = build(days);
  function build(entries: any[]): Week[] {
    if (!entries.length) return [];
    const byDate = new Map(entries.map((e) => [e.date, e]));
    const first = new Date(entries[0].date + 'T12:00:00');
    const last = new Date(entries[entries.length - 1].date + 'T12:00:00');
    const start = new Date(first);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    const out: Week[] = [];
    for (const d = new Date(start); d <= last; d.setDate(d.getDate() + 1)) {
      if (!out.length || out[out.length - 1].days.length === 7) out.push({ number: out.length + 1, from: iso(d), to: iso(d), days: [] });
      const week = out[out.length - 1];
      const e = byDate.get(iso(d));
      week.days.push(e ? { date: iso(d), rest: !!e.rest_day, rings: ringsOf(e) } : null);
      week.to = iso(d);
    }
    return out;
  }
  const kindName = (k: Kind) => $_(({ good: 'report.consistency.on_target', warn: 'report.consistency.close', alert: 'report.consistency.far_off', rest: 'report.weeks.rest', rest_trained: 'report.weeks.rest_trained' } as const)[k]);
  /** What a day says, in words: for a screen reader and the hover text. */
  function dayText(day: NonNullable<Day>): string {
    const rings = day.rings ?? [];
    const date = fmtDate(day.date, 'short');
    if (!rings.length) return date;
    if (rings[0].kind === 'rest' || rings[0].kind === 'rest_trained') return `${date}: ${kindName(rings[0].kind)}`;
    return $_('report.weeks.day_label', { values: { date, overall: kindName(rings[0].kind), low: kindName(rings[1].kind), high: kindName(rings[2].kind), peak: kindName(rings[3].kind) } });
  }
  // Good: a solid ring; close: a dashed ring; far off target: a filled disc (rest days: a solid ring in grey or violet).
  const ringStyle = (r: { kind: Kind; color: string }) => (r.kind === 'alert' ? `background:${r.color};border-color:${r.color}` : `border-color:${r.color};${r.kind === 'warn' ? 'border-style:dashed' : ''}`);
  $: DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d) => $_('report.weeks.' + d));
</script>

<div class="overflow-x-auto">
  <div class="inline-flex gap-1.5 min-w-full">
    <div class="shrink-0 w-9 pt-[34px] text-xs text-zinc-700">
      {#each DAYS as d}<div class="h-[22px] leading-[22px]">{d}</div>{/each}
    </div>
    {#each weeks as w}
      <div class="flex-1 min-w-[58px] text-center">
        <div class="text-xs font-medium text-zinc-700">{$_('report.weeks.week', { values: { n: w.number } })}</div>
        <div class="text-[10px] text-zinc-400 mb-1 whitespace-nowrap">{fmtDate(w.from, 'short')} – {fmtDate(w.to, 'short')}</div>
        <div class="rounded bg-zinc-50 py-1">
          {#each w.days as day}
            <div class="h-[22px] flex items-center justify-center gap-[3px]" role={day?.rings ? 'img' : undefined} aria-label={day?.rings ? dayText(day) : undefined} title={day?.rings ? dayText(day) : undefined}>
              {#if day?.rings}
                {#each day.rings as r}<span class="inline-block w-[10px] h-[10px] rounded-full border-2 border-solid bg-white" style={ringStyle(r)} aria-hidden="true" />{/each}
              {/if}
            </div>
          {/each}
        </div>
      </div>
    {/each}
  </div>
</div>
<div class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-600">
  <span class="inline-flex items-center gap-1"><span class="inline-block w-[10px] h-[10px] rounded-full border-2 border-solid bg-white" style={ringStyle({ kind: 'good', color: xssColors.deviationGood })} aria-hidden="true" /> {$_('report.consistency.on_target')}</span>
  <span class="inline-flex items-center gap-1"><span class="inline-block w-[10px] h-[10px] rounded-full border-2 border-solid bg-white" style={ringStyle({ kind: 'warn', color: xssColors.deviationWarn })} aria-hidden="true" /> {$_('report.consistency.close')}</span>
  <span class="inline-flex items-center gap-1"><span class="inline-block w-[10px] h-[10px] rounded-full border-2 border-solid bg-white" style={ringStyle({ kind: 'alert', color: xssColors.deviationAlert })} aria-hidden="true" /> {$_('report.consistency.far_off')}</span>
  <span class="inline-flex items-center gap-1"><span class="inline-block w-[10px] h-[10px] rounded-full border-2 border-solid bg-white" style={ringStyle({ kind: 'rest', color: GREY })} aria-hidden="true" /> {$_('report.weeks.rest')}</span>
  <span class="inline-flex items-center gap-1"><span class="inline-block w-[10px] h-[10px] rounded-full border-2 border-solid bg-white" style={ringStyle({ kind: 'rest_trained', color: VIOLET })} aria-hidden="true" /> {$_('report.weeks.rest_trained')}</span>
  <span class="text-zinc-500">{$_('report.weeks.rings')}</span>
</div>
